// Import general mixin for shared functionality.
import mixin from './mixin.js';

// Import general components..
import Skeleton from './components/Skeleton.vue';

// Import general functions for shared functionality.
import constants from '../constants.js';

// Import axios for API consuming in a clean interface.
import axios from 'axios';

// Swiper
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

Swiper.use([Pagination, Navigation]);

const defaultApp = {
    mixins: [mixin],
    components: { Skeleton },
    data() {
        return {
            dataApi: '',
            dataLimit: 15,
            dataSortKey: '',
            dataSortDirection: '',
            dataAmountsSentence: '',
            dataWidget: false,
            dataOverruleIds: [],
            dataThemeId: null,
            dataRegionId: null,
            dataAuthorId: null,
            dataPeriodId: null,
            dataOrganizationId: null,
            dataCulture: 'nl-NL',
            loading: false,
            loadingMore: false,
            debounceSetLoading: null,
            data: {
                items: [],
                facets: [],
                queryCount: 0,
            },
            page: 1,
            pageLimit: 100,
            searchText: '',
            filterParams: null,
            dropdownHandlers: new Map(),
            swiperInstance: null,
            windowWidth: window.innerWidth,

            constants: constants,
        }
    },
    // Watcher for pushing view preference to cookie for returning visitors.
    watch: {
        'data.facets': {
            handler(newVal) {
                if (newVal && newVal.length) {
                    this.$nextTick(() => { 
                        this.initDropdowns();
                        this.initForm();
                    });
                }
            },
            immediate: true,
            deep: true
        }
    },
    // The computed object holds checks for facets set, filters chosen and filters active.
    // Furthermore, reactive properties for orderItems, filterItems and the queryModel are kept track of.
    computed: {
        facetsSet() {
            return this.data.facets.length;
        },
        itemsSet() {
            return this.data.items.length;
        },
        filtersChosen() {
            return (this.facetsSet && this.data.facets.some(facet => facet.values.length)) || this.dataOverruleIds.length;
        },
        filtersActive() {
            return (this.facetsSet && this.data.facets.some(facet => facet.selected)) || this.searchText;
        },
        orderItems() {
            if (!this.dataSortKey && !this.dataSortDirection || this.searchText) return [];
            return [
                {
                    Key: this.dataSortKey,
                    Direction: this.dataSortDirection,
                }
            ]
        },
        filterItems() {
            let filters = [];

            if (this.filterParams) {
                filters = Array.from(this.filterParams.entries()).map(([key, value]) => ({
                    Key: key,
                    Values: value.split(','),
                    Operator: this.constants.operators.in,
                }));
            } else if (this.dataOverruleIds.length) {
                return [
                    {
                        Key: this.constants.keys.id,
                        Values: this.dataOverruleIds,
                        Operator: this.constants.operators.in,
                    }
                ];
            } else {
                filters = this.data.facets.map(facet => {
                    if (facet.values.length) {
                        return {
                            Key: facet.alias,
                            Values: facet.values,
                            Operator: this.constants.operators.in,
                        };
                    }
                }).filter(Boolean);
            }

            const extraFilters = [
                { id: this.dataThemeId, key: this.constants.keys.theme },
                { id: this.dataRegionId, key: this.constants.keys.region },
                { id: this.dataAuthorId, key: this.constants.keys.author },
                { id: this.dataPeriodId, key: this.constants.keys.period },
                { id: this.dataOrganizationId, key: this.constants.keys.organization },
            ].filter(item => item.id).map(item => ({
                Key: item.key,
                Values: [item.id],
                Operator: this.constants.operators.in,
            }));

            return filters.concat(extraFilters);
        },
        queryModel() {
            return {
                Limit: this.dataLimit,
                Offset: this.itemsSet && !this.dataWidget ? (this.page - 1) * this.dataLimit : 0,
                OrderItems: this.orderItems,
                FilterItems: this.filterItems,
                Text: this.searchText
            }
        },
        amountsSentence() {
            if (!this.data || !this.data.queryCount) return null;
            return this.dataAmountsSentence
                .replace(this.constants.magicStrings.queryCount, this.data.items.length)
                .replace(this.constants.magicStrings.totalCount, this.data.queryCount);
        },
        selectFacets() {
            if (!this.facetsSet) return [];
            return this.data.facets.filter(facet => facet.inputType === this.constants.inputTypes.select && facet.options.length);
        },
    },
    methods: {
        async init() {
            // Set data props according to data attributes in HTML template.
            if (this.$el.dataset.widget) this.dataWidget = true;
            if (this.$el.dataset.themeId) this.dataThemeId = this.$el.dataset.themeId;
            if (this.$el.dataset.regionId) this.dataRegionId = this.$el.dataset.regionId;
            if (this.$el.dataset.authorId) this.dataAuthorId = this.$el.dataset.authorId;
            if (this.$el.dataset.periodId) this.dataPeriodId = this.$el.dataset.periodId;
            if (this.$el.dataset.organizationId) this.dataOrganizationId = this.$el.dataset.organizationId;
            if (this.$el.dataset.overruleIds) this.dataOverruleIds = this.$el.dataset.overruleIds.split(',');
            if (this.$el.dataset.culture) this.dataCulture = this.$el.dataset.culture;
            if (this.$el.dataset.api) this.dataApi = this.$el.dataset.api + '?culture=' + this.dataCulture;
            if (this.$el.dataset.limit) this.dataLimit = parseInt(this.$el.dataset.limit);
            if (this.$el.dataset.sortKey) this.dataSortKey = this.$el.dataset.sortKey;
            if (this.$el.dataset.sortDirection) this.dataSortDirection = this.$el.dataset.sortDirection;
            if (this.$el.dataset.amountsSentence) this.dataAmountsSentence = this.$el.dataset.amountsSentence;

            // Construct query model and alter data props based on URL search params.
            const urlParams = new URLSearchParams(window.location.search);

            let pageParam = urlParams.has(this.constants.queryParams.page) ? parseInt(urlParams.get(this.constants.queryParams.page)) : 0;
            if (pageParam > this.pageLimit) pageParam = this.pageLimit;
            if (pageParam) urlParams.delete(this.constants.queryParams.page);
            if (pageParam < 2) pageParam = 0;
            this.searchText = urlParams.has(this.constants.queryParams.text) ? urlParams.get(this.constants.queryParams.text) : '';
            if (this.searchText) urlParams.delete(this.constants.queryParams.text);
            let filterParams = urlParams.toString() ? urlParams : null;
            this.filterParams = filterParams;

            // Prefetch data for overrule ids.
            if (this.dataOverruleIds.length) {
                const overruleResponse = await this.fetchData();
                // Make sure ordering corresponds to picking order in CMS.
                if (overruleResponse && overruleResponse.data && overruleResponse.data.items && overruleResponse.data.items.length) {
                    overruleResponse.data.items = overruleResponse.data.items.sort((a, b) => this.dataOverruleIds.indexOf(a.id) - this.dataOverruleIds.indexOf(b.id));
                }
                this.setData(overruleResponse);
                this.dataOverruleIds = [];
            }

            let tempLimit = this.dataLimit;
            if (pageParam && !this.dataWidget) {
                this.page = pageParam;
                this.dataLimit = this.dataLimit * this.page;
            }

            // Initial data fetch.
            const response = await this.fetchData();
            let replace = !this.itemsSet;
            this.setData(response, replace);
            this.dataLimit = tempLimit;
            this.filterParams = null;

            // Remove duplicates from items.
            if (this.itemsSet) {
                this.data.items = this.removeDuplicatesById(this.data.items);
                if (this.dataWidget) this.data.items = this.data.items.slice(0, this.dataLimit);
            }

            this.initForm();

            this.$nextTick(() => {
                this.initSwiperIfNeeded();
            });
        },
        // The fetchData method fetches and handles API data, mainly facets and resulting items.
        async fetchData(loading = true) {
            if (loading) this.debounceSetLoading(true);
            let response = null;
            try {
                response = await axios.post(this.dataApi, this.queryModel);
            } catch (error) {
                console.log(error);
            } finally {
                if (loading) this.debounceSetLoading(false);
            }
            return response;
        },
        // The fetchInitialData method resets the data and page props, loads data anew and sets the data.
        async fetchInitialData() {
            this.page = 1;
            const response = await this.fetchData();
            this.setData(response, true);
            this.updateUrl();
        },
        // Lazy loading data chunks handler.
        async loadMore() {
            this.loadingMore = true;
            this.page++;
            const response = await this.fetchData(false);
            this.setData(response);
            this.loadingMore = false;
            this.updateUrl();
        },
        // Method to set data items array or append items to it.
        setData(response, replace = false) {
            if (!response || response.status != 200 || !response.data.items) return;
            if (!this.itemsSet || replace) {
                this.data.items = response.data.items;
            }
            else {
                response.data.items.forEach(item => {
                    this.data.items.push(item);
                })
            }
            this.setFacets(response);
        },
        // Method to set facets array.
        setFacets(response) {
            if (!response || response.status != 200 || !response.data.facets) return;
            this.data.facets = response.data.facets
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(facet => ({
                ...facet,
                values: facet.options.filter(option => option.selected).map(option => option.valueAlias),
            }));
            this.data.queryCount = response.data.queryCount;
        },
        // Method for removing duplicates from array by id.
        removeDuplicatesById(array) {
            return array.reduce((accumulator, current) => {
                if (!accumulator.find((item) => item.id === current.id)) {
                    accumulator.push(current);
                }
                return accumulator;
            }, []);
        },
        // Data alteration functions for reloading data with set filters.
        selectOption(facetAlias, optionAlias) {
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            if (!changedFacet) return;
            changedFacet.options.forEach(option => {
                option.selected = (option.valueAlias === optionAlias);
            });
            changedFacet.values = [optionAlias];
            this.loading = true;
            this.fetchInitialData();
            this.loading = false;
        },
        selectButton(facetAlias, optionAlias, event) {
            event.target.blur();
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            if (!changedFacet) return;
            if (changedFacet.values.includes(optionAlias)) {
                changedFacet.values = changedFacet.values.filter(value => value !== optionAlias);
            } else {
                changedFacet.values.push(optionAlias);
            }
            changedFacet.options.forEach(option => {
                option.selected = changedFacet.values.includes(option.valueAlias);
            });
            this.loading = true;
            this.fetchInitialData();
            this.loading = false;
        },
        // Data alteration function for resetting all active filters and reloading data.
        reset() {
            this.data.facets.forEach(facet => facet.values = []);
            this.searchText = '';
            this.loading = true;
            this.fetchInitialData();
            this.loading = false;
        },
        // Data alteration function for resetting a single filter and reloading data.
        resetFilter(facetAlias, event) {
            event.target.blur();
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            if (!changedFacet) return;
            changedFacet.values = [];
            changedFacet.options.forEach(option => option.selected = false);
            this.loading = true;
            this.fetchInitialData();
            this.loading = false;
        },
        // Function for updating browser URL and state history with changed filters, search input or page.
        updateUrl() {
            if (this.dataWidget) return;
            if (!this.queryModel.Text && !this.queryModel.FilterItems.length && this.page === 1) {
                history.pushState({}, null, window.location.pathname);
                return;
            }
            const urlParams = new URLSearchParams();
            if (this.queryModel.Text) urlParams.append(this.constants.queryParams.text, this.queryModel.Text);
            if (this.queryModel.FilterItems.length) {
                this.queryModel.FilterItems.forEach(filterItem => urlParams.append(filterItem.Key, filterItem.Values.join()))
            }
            if (this.page > 1) urlParams.append(this.constants.queryParams.page, this.page);
            history.pushState({}, null, window.location.pathname + '?' + urlParams.toString());
        },
        createDebouncedLoading() {
            return this.debounce((state) => {
                this.loading = state;
            }, 500);
        },
        initForm() {
            const form = this.$el.querySelector('[data-prevent-submit]');
            if (form) form.addEventListener('submit', e => e.preventDefault());
        },
        initDropdowns() {
            const $dropdowns = this.$el.querySelectorAll('.dropdown');

            $dropdowns.forEach($dropdown => {
                const $trigger = $dropdown.querySelector('.dropdown-trigger');

                if ($trigger) {
                    if (this.dropdownHandlers.has($dropdown)) {
                        $trigger.removeEventListener('click', this.dropdownHandlers.get($dropdown));
                    }

                    const boundHandler = this.toggleDropdown.bind(this, $dropdown);
                    this.dropdownHandlers.set($dropdown, boundHandler);

                    $trigger.addEventListener('click', boundHandler);
                }
            });

            if (!this._documentClickListener) {
                this._documentClickListener = this.closeAllDropdowns.bind(this);
                document.addEventListener('click', this._documentClickListener);
            }
        },
        toggleDropdown($dropdown, event) {
            event.stopPropagation();
            const $dropdowns = document.querySelectorAll('.dropdown.is-active');
            $dropdowns.forEach($el => {
                if ($el !== $dropdown) {
                    $el.classList.remove('is-active');
                }
            });
            $dropdown.classList.toggle('is-active');
        },
        closeAllDropdowns() {
            const $dropdowns = document.querySelectorAll('.dropdown.is-active');
            $dropdowns.forEach($el => $el.classList.remove('is-active'));
        },
        initSwiperIfNeeded() {
            if (this.windowWidth < this.constants.breakpoints.tablet && !this.swiperInstance && this.$refs.swiper) {
                this.swiperInstance = new Swiper(this.$refs.swiper, {
                    slidesPerView: 1.2,
                    spaceBetween: 0,
                    navigation: {
                        nextEl: this.$el.querySelector('.swiper-button-next'),
                        prevEl: this.$el.querySelector('.swiper-button-prev'),
                    },
                    pagination: {
                        el: this.$el.querySelector('.swiper-pagination'),
                        type: 'fraction',
                        renderFraction: (currentClass, totalClass) => {
                            return `<span class="${currentClass}"></span><span></span>/<span class="${totalClass}"></span>`;
                        }
                    },
                    breakpoints: {
                        [this.constants.breakpoints.mobile]: {
                            slidesPerView: 2.2,
                        }
                    }
                });
            } else if (this.windowWidth >= this.constants.breakpoints.tablet) {
                this.destroySwiper();
            }
        },
        destroySwiper() {
            if (this.swiperInstance) {
                this.swiperInstance.destroy(true, true);
                this.swiperInstance = null;
            }
        },
        handleResize() {
            this.windowWidth = window.innerWidth;
            this.initSwiperIfNeeded();
        },
    },
    created() {
        // Data alteration function for reloading data with textual search input.
        this.changeSearchText = this.debounce(() => {
            this.fetchInitialData();
        });
        // Initialize the debounced loading function.
        this.debounceSetLoading = this.createDebouncedLoading();
    },
    beforeUnmount() {
        this.destroySwiper();
        window.removeEventListener('resize', this.handleResize);
    },
    // Call init method in mounted hook.
    mounted() {
        this.init();
        window.addEventListener('resize', this.handleResize);
    },
    updated() {
        this.initDropdowns();
    }
};

export default defaultApp;
